/**
 * ChecklistPublishDialog - Confirmação de publicação de nova versão
 *
 * Mostra o resumo calculado no banco (adicionados, removidos, alterados) e
 * os erros que impedem a publicação. Versões anteriores ficam intactas.
 *
 * @version 1.1.0
 * RELEASE: 2026-09-26 v1.1.0 - Ações padronizadas no tamanho compacto de Departamentos.
 * RELEASE: 2026-09-26 v1.0.0 - Criação inicial (Fase 1b)
 */
import { Rocket } from 'lucide-react';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { InlineNotice } from '@/components/ui/inline-notice';
import { useChecklistPublishPreview } from '@/hooks/useChecklistTemplates';

interface Props {
  open: boolean;
  onOpenChange: (o: boolean) => void;
  templateId: string;
  publishing: boolean;
  onConfirm: () => Promise<unknown>;
}

export function ChecklistPublishDialog({ open, onOpenChange, templateId, publishing, onConfirm }: Props) {
  const { data, isLoading } = useChecklistPublishPreview(templateId, open);
  const blocked = !data || data.errors.length > 0;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Publicar versão {data?.next_version_number ?? ''}</DialogTitle>
          <DialogDescription>
            Novas vistorias passam a usar esta versão. Vistorias já criadas continuam na versão em que foram abertas.
          </DialogDescription>
        </DialogHeader>

        {isLoading || !data ? (
          <Skeleton className="h-24 w-full" />
        ) : (
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-2 text-sm">
              <Stat label="Itens na versão" value={data.items_active} />
              <Stat label="Adicionados" value={data.items_added} />
              <Stat label="Alterados" value={data.items_changed} />
              <Stat label="Retirados" value={data.items_removed} />
            </div>
            {data.errors.map((e) => <InlineNotice key={e} variant="error">{e}</InlineNotice>)}
            {data.current_version_number && !data.has_unpublished_changes && data.errors.length === 0 && (
              <InlineNotice variant="info">Não há alterações desde a versão {data.current_version_number}.</InlineNotice>
            )}
          </div>
        )}

        <DialogFooter>
          <Button size="xs" variant="outline" onClick={() => onOpenChange(false)}>Cancelar</Button>
          <Button
            size="xs"
            disabled={blocked || publishing}
            onClick={async () => { await onConfirm(); onOpenChange(false); }}
          >
            <Rocket className="mr-1.5 h-4 w-4" /> Publicar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Stat({ label, value }: { label: string; value: number }) {
  return (
    <div className="rounded-md border px-3 py-2">
      <div className="text-xs text-muted-foreground">{label}</div>
      <div className="text-lg font-semibold">{value}</div>
    </div>
  );
}
